import React, { useEffect, useRef, useState } from "react";
import { fetchAutoRenewInternalDisplay, fetchAutoRenewRequest, submitAutoRenewRequest } from "../utils/api";
import { fmtDateOnly } from "../utils/dates";

function completedEffectiveDate(value) {
  if (typeof value !== "string" || value.length !== 10) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // The shared formatter accepts prefixes, rolls invalid days forward, and
  // maps years 1-99 into the 1900s. Suppress those instead of showing a
  // different effective date; keep this guard local to the confirmation.
  if (year < 100 || month < 1 || month > 12) return "";
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day < 1 || day > days[month - 1]) return "";
  return fmtDateOnly(value, { month: "long", day: "numeric", year: "numeric" }, "");
}

/** Compact Account-only confirmation of a server-attested next-term request. */
export function AutoRenewCompletedRow({ slug, isInternal = false }) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    if (slug) {
      const read = isInternal ? fetchAutoRenewInternalDisplay : fetchAutoRenewRequest;
      Promise.resolve().then(() => read(slug))
        .then(view => { if (active) setResult({ slug, isInternal, view }); })
        .catch(() => { if (active) setResult(null); });
    }
    return () => { active = false; };
  }, [slug, isInternal]);

  const view = result?.slug === slug && result?.isInternal === isInternal ? result.view : null;
  const effectiveDate = view?.enabled === true && view.state === "completed"
    ? completedEffectiveDate(view.effective_on) : "";
  if (!effectiveDate) return null;

  return (
    <dl aria-label="Auto-renew" style={{ margin: 0, padding: "10px 0", fontSize: 13 }}>
      <dt style={{ color: "#aab8cd", marginBottom: 4 }}>Auto-renew</dt>
      <dd style={{ margin: 0 }}>
        Patriot completed your auto-renew request. The change is set to take effect on {effectiveDate},
        when your next contract starts. The 5% savings applies to that renewed contract.
      </dd>
    </dl>
  );
}

function localRequestedOn(value) {
  if (!value) return "";
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium", timeStyle: "short",
  }).format(instant);
}

/** A server-authorized customer request, never a contract change. */
export default function AutoRenewOffer({ slug, isInternal = false }) {
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const selection = useRef({ slug, isInternal });
  selection.current = { slug, isInternal };
  const view = result?.slug === slug && result?.isInternal === isInternal ? result.view : null;

  const accept = (readSlug, readInternal, nextView) => {
    if (selection.current.slug === readSlug && selection.current.isInternal === readInternal) {
      setResult({ slug: readSlug, isInternal: readInternal, view: nextView });
    }
  };

  useEffect(() => {
    let active = true;
    setResult(null);
    if (slug) {
      const read = isInternal ? fetchAutoRenewInternalDisplay : fetchAutoRenewRequest;
      Promise.resolve().then(() => read(slug))
        .then(nextView => {
          if (active && selection.current.slug === slug &&
              selection.current.isInternal === isInternal) {
            setResult({ slug, isInternal, view: nextView });
          }
        })
        .catch(() => { if (active) setResult(null); });
    }
    return () => { active = false; };
  }, [slug, isInternal]);

  const submit = async () => {
    if (isInternal || inFlight.current || view?.state !== "eligible" || !view.offer_precondition) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      accept(slug, isInternal, await submitAutoRenewRequest(slug, view.offer_precondition));
    } catch {
      // A timeout can mean the request was created. Hide the button before
      // checking status, and never offer an automatic second submission.
      accept(slug, isInternal, { enabled: true, state: "uncertain" });
      try {
        const current = await fetchAutoRenewRequest(slug);
        if (current?.state !== "eligible") accept(slug, isInternal, current);
      } catch {
        // Keep the uncertainty message when even the status read fails.
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (!view?.enabled || !["eligible", "pending", "contact", "uncertain"].includes(view.state)
      || (view.state === "eligible" && !isInternal && !view.offer_precondition)) {
    return null;
  }
  const requestedOn = localRequestedOn(view.requested_on);
  return (
    <section aria-label="Auto-renew request" style={{
      border: "1px solid rgba(148,163,184,.22)", borderRadius: 10,
      background: "rgba(68,166,255,.04)", color: "#edf4ff", padding: "10px 12px",
      marginBottom: 12, fontSize: 13, lineHeight: 1.45,
      ...(view.state === "eligible" ? { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 } : {}),
    }}>
      <div style={view.state === "eligible" ? { flex: "1 1 520px", minWidth: 0 } : undefined}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Request auto-renew</div>
        {view.state === "eligible" && <p style={{ margin: 0 }}>{view.offer_text}</p>}
        {view.state === "pending" && (
          <p style={{ margin: 0 }}>
            We received your auto-renew request{requestedOn ? ` on ${requestedOn}` : ""}.
            {" "}Patriot will follow up to update your contract. Your current contract has not changed.
          </p>
        )}
        {view.state === "contact" && (
          <p style={{ margin: 0 }}>
            Please contact Patriot about auto-renew for this term.
          </p>
        )}
        {view.state === "uncertain" && (
          <p style={{ margin: 0 }}>
            We couldn’t confirm your request status. Please contact Patriot before trying again.
          </p>
        )}
      </div>
      {view.state === "eligible" && <button type="button" onClick={isInternal ? undefined : submit} disabled={isInternal || submitting} style={{
        flex: "0 0 auto", boxSizing: "border-box", minHeight: 32,
        background: "rgba(68,166,255,.10)", border: "1px solid rgba(68,166,255,.40)",
        borderRadius: 6, color: "#edf4ff", fontSize: 13, fontWeight: 600,
        padding: "6px 10px", opacity: isInternal || submitting ? 0.7 : 1,
        cursor: isInternal ? "not-allowed" : submitting ? "wait" : "pointer",
      }}>{submitting ? "Submitting…" : "Request auto-renew"}</button>}
    </section>
  );
}
